import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Typography } from '@mui/material';
import SignaturePad, { SignaturePadHandle } from './SignaturePad';
import { faxSignaturesApi } from '../../services/api';

type Page = {
  index: number;
  widthPt: number;
  heightPt: number;
  imageDataUrl: string;
};

type Placement = {
  pageIndex: number;
  x: number;
  y: number;
  width: number;
  height: number;
};

type Props = {
  task: { id: string; patientName?: string };
  onClose: () => void;
  onDone: () => void | Promise<void>;
};

const SIG_WIDTH_FRAC = 0.28;

const SignFaxViewer: React.FC<Props> = ({ task, onClose, onDone }) => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pages, setPages] = useState<Page[]>([]);
  const [filename, setFilename] = useState('fax.pdf');
  const [returnFax, setReturnFax] = useState('');
  const [returnFaxCandidates, setReturnFaxCandidates] = useState<string[]>([]);
  const returnFaxEdited = useRef(false);
  const returnFaxInputRef = useRef<HTMLInputElement | null>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [practiceSigUrl, setPracticeSigUrl] = useState<string | null>(null);
  const [padHasInk, setPadHasInk] = useState(false);
  const [drawnUrl, setDrawnUrl] = useState<string | null>(null);
  const padRef = useRef<SignaturePadHandle | null>(null);
  const onPadDirty = useCallback((hasInk: boolean) => {
    setPadHasInk(hasInk);
    setDrawnUrl(hasInk ? padRef.current?.toDataURL('image/png') || null : null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [viewerRes, cfgRes] = await Promise.all([
          faxSignaturesApi.getViewer(task.id),
          faxSignaturesApi.get().catch(() => null),
        ]);
        if (cancelled) return;
        const data = viewerRes.data?.data || {};
        setPages(Array.isArray(data.pages) ? data.pages : []);
        setFilename(data.filename || 'fax.pdf');
        const cands = Array.isArray(data.returnFaxCandidates) ? data.returnFaxCandidates : [];
        setReturnFaxCandidates(cands);
        if (!returnFaxEdited.current) {
          setReturnFax(data.returnFax || '');
        }
        const cfg = cfgRes?.data?.data;
        if (cfg?.practiceSignatureUrl) setPracticeSigUrl(cfg.practiceSignatureUrl);
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.error || e?.message || 'Could not open fax');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [task.id]);

  const overlaySrc = useMemo(() => drawnUrl || practiceSigUrl, [drawnUrl, practiceSigUrl]);

  const placementPayload = () => {
    if (!placement) return null;
    const drawn = padHasInk ? padRef.current?.toDataURL('image/png') : undefined;
    return {
      returnFax,
      signatureDataUrl: drawn || undefined,
      pageIndex: placement.pageIndex,
      x: placement.x,
      y: placement.y,
      width: placement.width,
      height: placement.height,
    };
  };

  const onPageClick = (page: Page, ev: React.MouseEvent<HTMLDivElement>) => {
    const img = ev.currentTarget.querySelector('img');
    if (!img) return;
    const rect = img.getBoundingClientRect();
    const relX = (ev.clientX - rect.left) / rect.width;
    const relY = (ev.clientY - rect.top) / rect.height;
    if (relX < 0 || relY < 0 || relX > 1 || relY > 1) return;
    const width = Math.min(page.widthPt * SIG_WIDTH_FRAC, 220);
    const height = width * 0.38;
    const x = relX * page.widthPt;
    const y = page.heightPt - relY * page.heightPt - height;
    setPlacement({ pageIndex: page.index, x, y, width, height });
    setPreviewUrl(null);
    setError(null);
  };

  const overlayStyle = (page: Page): React.CSSProperties | null => {
    if (!placement || placement.pageIndex !== page.index) return null;
    return {
      position: 'absolute',
      left: `${(placement.x / page.widthPt) * 100}%`,
      bottom: `${(placement.y / page.heightPt) * 100}%`,
      width: `${(placement.width / page.widthPt) * 100}%`,
      height: `${(placement.height / page.heightPt) * 100}%`,
      border: '2px dashed #d32f2f',
      background: 'rgba(211,47,47,0.08)',
      pointerEvents: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
    };
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1500,
        bgcolor: 'rgba(15,23,42,0.72)',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center',
        p: { xs: 0.5, md: 1.5 },
      }}
    >
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 2,
          width: '100%',
          maxWidth: 1480,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          overflow: 'hidden',
          boxShadow: 8,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, bgcolor: '#e8e8e8', overflow: 'auto', p: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            {task.patientName || 'Fax'} — {filename}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            Scroll the whole fax. Click the left end of the signature line to place the stamp. Click again to move it.
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            pages.map((page) => (
              <Box
                key={page.index}
                onClick={(ev) => onPageClick(page, ev)}
                sx={{
                  position: 'relative',
                  mb: 2,
                  mx: 'auto',
                  maxWidth: 860,
                  cursor: 'crosshair',
                  boxShadow: 2,
                  bgcolor: '#fff',
                }}
              >
                <img
                  src={page.imageDataUrl}
                  alt={`Fax page ${page.index + 1}`}
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
                {overlayStyle(page) && (
                  <Box sx={overlayStyle(page) as any}>
                    {overlaySrc ? (
                      <img src={overlaySrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    ) : (
                      <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 700 }}>
                        Signature
                      </Typography>
                    )}
                  </Box>
                )}
                <Typography
                  variant="caption"
                  sx={{ position: 'absolute', top: 6, right: 8, bgcolor: 'rgba(255,255,255,0.85)', px: 0.75, borderRadius: 1 }}
                >
                  Page {page.index + 1} of {pages.length}
                </Typography>
              </Box>
            ))
          )}
        </Box>

        <Box
          sx={{
            width: { xs: '100%', md: 360 },
            flexShrink: 0,
            borderLeft: { md: '1px solid' },
            borderColor: 'divider',
            p: 2,
            overflow: 'auto',
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>Sign & return</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            Return fax number — type or correct this. Suggestion is a guess; you decide.
          </Typography>
          <Box
            component="input"
            ref={returnFaxInputRef}
            type="tel"
            inputMode="tel"
            autoComplete="off"
            spellCheck={false}
            readOnly={false}
            disabled={false}
            placeholder="Type the return fax number"
            value={returnFax}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              returnFaxEdited.current = true;
              setReturnFax(e.target.value);
            }}
            onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
            onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
            onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}
            sx={{
              width: '100%',
              boxSizing: 'border-box',
              font: 'inherit',
              fontSize: 16,
              px: 1.25,
              py: 1,
              mb: 0.75,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: '#fff',
              outline: 'none',
              '&:focus': { borderColor: 'primary.main', boxShadow: '0 0 0 3px rgba(25,118,210,0.18)' },
            }}
          />
          {returnFaxCandidates.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
              {returnFaxCandidates.map((n) => (
                <Chip
                  key={n}
                  size="small"
                  label={n}
                  variant={returnFax === n ? 'filled' : 'outlined'}
                  onClick={() => {
                    returnFaxEdited.current = true;
                    setReturnFax(n);
                    returnFaxInputRef.current?.focus();
                  }}
                />
              ))}
            </Box>
          )}
          <SignaturePad
            ref={padRef}
            width={300}
            height={110}
            label="Draw for this fax (optional — otherwise practice signature)"
            onDirtyChange={onPadDirty}
            disabled={busy}
          />
          <Typography variant="body2" sx={{ mt: 1.5, mb: 1 }}>
            {placement
              ? `Placed on page ${placement.pageIndex + 1}.`
              : 'No placement yet — click the signature line on the fax.'}
          </Typography>
          {error && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{error}</Typography>}
          {previewUrl && (
            <Button size="small" href={previewUrl} target="_blank" rel="noopener" sx={{ mb: 1 }}>
              Open stamped PDF preview
            </Button>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
            <Button
              variant="outlined"
              disabled={busy || !placement}
              onClick={async () => {
                const payload = placementPayload();
                if (!payload) return;
                setBusy(true); setError(null);
                try {
                  const res = await faxSignaturesApi.prepareSign(task.id, payload);
                  const b64 = res.data?.data?.signedPdfBase64;
                  if (b64) setPreviewUrl(`data:application/pdf;base64,${b64}`);
                  if (!returnFax && res.data?.data?.returnFax) setReturnFax(res.data.data.returnFax);
                } catch (e: any) {
                  setError(e?.response?.data?.error || e?.message || 'Preview failed');
                } finally { setBusy(false); }
              }}
            >
              Preview stamp
            </Button>
            <Button
              variant="contained"
              disabled={busy || !placement || !returnFax}
              onClick={async () => {
                const payload = placementPayload();
                if (!payload) return;
                setBusy(true); setError(null);
                try {
                  await faxSignaturesApi.signAndReturn(task.id, {
                    ...payload,
                    coverText: `Signed document — ${task.patientName || 'form'}`,
                  });
                  await onDone();
                } catch (e: any) {
                  setError(e?.response?.data?.error || e?.message || 'Send failed');
                } finally { setBusy(false); }
              }}
            >
              {busy ? 'Working…' : 'Sign & fax back'}
            </Button>
            <Button disabled={busy} onClick={onClose}>Cancel</Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SignFaxViewer;
